import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Theme types for type safety
 */
export type ThemeMode = 'light' | 'dark' | 'system';

export interface ThemeContextType {
  /** Current theme mode */
  mode: ThemeMode;
  /** Actual theme being applied (resolved from system if mode is 'system') */
  resolvedTheme: 'light' | 'dark';
  /** Function to change theme mode */
  setMode: (mode: ThemeMode) => void;
  /** Toggle between light and dark (skips system) */
  toggleTheme: () => void;
  /** Whether theme is being loaded from localStorage */
  isLoading: boolean;
}

/**
 * Theme context with default values
 */
const ThemeContext = createContext<ThemeContextType>({
  mode: 'system',
  resolvedTheme: 'light',
  setMode: () => {},
  toggleTheme: () => {},
  isLoading: true,
});

/**
 * Custom hook to use theme context
 * @returns Theme context values and methods
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Storage key for theme persistence
 */
const THEME_STORAGE_KEY = 'bombard-theme';

/**
 * Get system preference for color scheme
 * @returns 'light' or 'dark' based on system preference
 */
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * Get stored theme from localStorage
 * @returns Stored theme mode or 'system' as default
 */
const getStoredTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored as ThemeMode;
    }
  } catch (error) {
    console.warn('Failed to read theme from localStorage:', error);
  }
  return 'system';
};

/**
 * Store theme in localStorage
 * @param mode Theme mode to store
 */
const storeTheme = (mode: ThemeMode): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch (error) {
    console.warn('Failed to store theme in localStorage:', error);
  }
};

/**
 * Apply theme to document element
 * @param theme Theme to apply ('light' or 'dark')
 */
const applyTheme = (theme: 'light' | 'dark'): void => {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  const isDark = theme === 'dark';
  
  // Toggle dark class for Tailwind
  root.classList.toggle('dark', isDark);
  
  // Set color-scheme for native elements
  root.style.colorScheme = theme;
  
  // Set CSS custom properties for additional theming
  root.style.setProperty('--theme-mode', theme);
  
  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', isDark ? '#0a0a0a' : '#ffffff');
  } else {
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = isDark ? '#0a0a0a' : '#ffffff';
    document.head.appendChild(meta);
  }
};

/**
 * Props for ThemeProvider component
 */
interface ThemeProviderProps {
  children: ReactNode;
  /** Default theme mode (defaults to 'system') */
  defaultMode?: ThemeMode;
  /** Disable automatic system theme detection */
  disableSystemDetection?: boolean;
  /** Custom storage key */
  storageKey?: string;
}

/**
 * Theme Provider Component
 * Provides theme context and handles theme switching, persistence, and system detection
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultMode = 'system',
  disableSystemDetection = false,
  storageKey = THEME_STORAGE_KEY,
}) => {
  const [mode, setModeState] = useState<ThemeMode>(defaultMode);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');
  const [isLoading, setIsLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  /**
   * Resolve theme based on mode and system preference
   */
  const resolveTheme = (currentMode: ThemeMode): 'light' | 'dark' => {
    if (currentMode === 'system') {
      return disableSystemDetection ? 'light' : getSystemTheme();
    }
    return currentMode;
  };

  /**
   * Update theme mode with animation
   */
  const setMode = (newMode: ThemeMode) => {
    const newResolvedTheme = resolveTheme(newMode);
    const isThemeChanging = newResolvedTheme !== resolvedTheme;

    if (isThemeChanging) {
      setIsTransitioning(true);
      // Small delay to allow transition animation
      setTimeout(() => setIsTransitioning(false), 150);
    }

    setModeState(newMode);
    setResolvedTheme(newResolvedTheme);
    storeTheme(newMode);
    applyTheme(newResolvedTheme);
  };

  /**
   * Toggle between light and dark modes
   */
  const toggleTheme = () => {
    const currentResolved = resolveTheme(mode);
    setMode(currentResolved === 'dark' ? 'light' : 'dark');
  };

  /**
   * Handle system theme changes
   */
  useEffect(() => {
    if (disableSystemDetection || mode !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? 'dark' : 'light';
      setResolvedTheme(newTheme);
      applyTheme(newTheme);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [mode, disableSystemDetection]);

  /**
   * Initialize theme on mount
   */
  useEffect(() => {
    // Get stored theme or use default
    const storedMode = getStoredTheme();
    const initialMode = storedMode || defaultMode;
    const initialResolvedTheme = resolveTheme(initialMode);

    setModeState(initialMode);
    setResolvedTheme(initialResolvedTheme);
    applyTheme(initialResolvedTheme);
    setIsLoading(false);
  }, [defaultMode, disableSystemDetection]);

  const contextValue: ThemeContextType = {
    mode,
    resolvedTheme,
    setMode,
    toggleTheme,
    isLoading,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <AnimatePresence mode="wait">
        <motion.div
          key={resolvedTheme}
          initial={isTransitioning ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.15,
            ease: 'easeInOut',
          }}
          className="theme-transition-wrapper"
          style={{
            // Ensure smooth transitions for theme changes
            transition: 'background-color 150ms ease-in-out, border-color 150ms ease-in-out',
          }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </ThemeContext.Provider>
  );
};

/**
 * Theme Toggle Button Component
 * A pre-built component for theme switching with smooth animations
 */
interface ThemeToggleProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  variant?: 'button' | 'icon';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = '',
  size = 'md',
  showLabel = false,
  variant = 'button',
}) => {
  const { mode, resolvedTheme, setMode, toggleTheme } = useTheme();

  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
  };

  const handleClick = () => {
    if (variant === 'icon') {
      toggleTheme();
    } else {
      // Cycle through system -> light -> dark
      const nextMode: ThemeMode = 
        mode === 'system' ? 'light' : 
        mode === 'light' ? 'dark' : 'system';
      setMode(nextMode);
    }
  };

  const getIcon = () => {
    if (mode === 'system') {
      return (
        <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
    }
    
    return resolvedTheme === 'dark' ? (
      <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    ) : (
      <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    );
  };

  const getLabel = () => {
    return mode === 'system' ? 'System' : 
           resolvedTheme === 'dark' ? 'Dark' : 'Light';
  };

  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={handleClick}
      className={`
        inline-flex items-center justify-center
        ${sizeClasses[size]}
        rounded-lg
        bg-neutral-100 dark:bg-neutral-800
        text-neutral-700 dark:text-neutral-300
        hover:bg-neutral-200 dark:hover:bg-neutral-700
        border border-neutral-200 dark:border-neutral-700
        transition-all duration-150 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        dark:focus:ring-offset-neutral-900
        ${className}
      `}
      title={`Switch to ${mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'} mode`}
    >
      <motion.div
        key={resolvedTheme + mode}
        initial={{ rotate: -30, opacity: 0 }}
        animate={{ rotate: 0, opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="p-1"
      >
        {getIcon()}
      </motion.div>
      {showLabel && (
        <span className="ml-2 text-sm font-medium">
          {getLabel()}
        </span>
      )}
    </motion.button>
  );
}; 